/**
 * The side-effecting half of the app-wide text context menu: Copy / Cut /
 * Paste / Select all on the target that `resolveTextMenu` described.
 *
 * Edits go through `document.execCommand('insertText' | 'delete')` first. It
 * acts on the focused field's current selection, keeps the undo stack, and
 * fires a real `input` event, so React-controlled inputs and the
 * SyntaxHighlightInput / Lexical contenteditables all see the change. Where it
 * is missing or refuses (jsdom, an engine that dropped it), fields fall back to
 * the native value setter + an `input` event — the only way a programmatic
 * value change reaches React's onChange — and contenteditables to a Range
 * splice.
 *
 * Paste is offered to the target's own `paste` handlers FIRST, as a synthetic
 * ClipboardEvent carrying the text, so menu Paste behaves exactly like Ctrl+V
 * wherever a component intercepts paste (the URL field's plain-text hook,
 * Lexical's rich paste). Only when nobody claims it is the text inserted here.
 */
import { copyTextToClipboard } from './clipboard';
import { isTauri } from './isTauri';

function exec(command, arg) {
  try {
    return typeof document.execCommand === 'function' && document.execCommand(command, false, arg) === true;
  } catch {
    return false;
  }
}

/** Put focus and the selection back where they were when the menu opened. */
export function restoreSelection(model) {
  const { el, kind } = model;
  if (!el) return;
  try {
    el.focus({ preventScroll: true });
  } catch {
    // ignore
  }
  if (kind === 'field') {
    try {
      el.setSelectionRange(model.start, model.end);
    } catch {
      // not selectable
    }
  } else if (kind === 'editable' && model.range && el.contains(model.range.startContainer)) {
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(model.range);
  }
}

function setFieldValue(field, value) {
  const proto = field.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(field, value);
  else field.value = value;
}

function spliceField(field, text) {
  const value = String(field.value ?? '');
  let start = value.length;
  let end = value.length;
  try {
    start = field.selectionStart ?? start;
    end = field.selectionEnd ?? end;
  } catch {
    // keep the end-of-value default
  }
  setFieldValue(field, value.slice(0, start) + text + value.slice(end));
  try {
    field.setSelectionRange(start + text.length, start + text.length);
  } catch {
    // ignore
  }
  field.dispatchEvent(new Event('input', { bubbles: true }));
}

function spliceEditable(host, text) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !host.contains(sel.anchorNode)) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  if (text) {
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
  host.dispatchEvent(new Event('input', { bubbles: true }));
}

/** Replace the current selection in `el` with `text` (empty = delete it). */
export function insertText(el, text) {
  if (text ? exec('insertText', text) : exec('delete')) return;
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') spliceField(el, text);
  else spliceEditable(el, text);
}

/** Give the target's own paste handlers the first go. True = they took it. */
export function dispatchPaste(el, text) {
  try {
    if (typeof DataTransfer !== 'function' || typeof ClipboardEvent !== 'function') return false;
    const data = new DataTransfer();
    data.setData('text/plain', text);
    const event = new ClipboardEvent('paste', { clipboardData: data, bubbles: true, cancelable: true });
    // An engine that ignores `clipboardData` in the init dict would hand the
    // handlers a null payload; insert it ourselves instead.
    if (event.clipboardData?.getData('text/plain') !== text) return false;
    el.dispatchEvent(event);
    return event.defaultPrevented;
  } catch {
    return false;
  }
}

/**
 * The clipboard as text, or '' when it is empty or unreadable. The desktop
 * app reads through the Tauri plugin (no user-activation gate, no WKWebView
 * prompt — see utils/compareSources.js); the browser build uses the async API.
 */
export async function readClipboardText() {
  if (isTauri()) {
    try {
      const { readText } = await import('@tauri-apps/plugin-clipboard-manager');
      return String((await readText()) ?? '');
    } catch {
      // fall through to the web API
    }
  }
  try {
    return String((await navigator.clipboard.readText()) ?? '');
  } catch {
    return '';
  }
}

export async function copyAction(model) {
  if (model.text) await copyTextToClipboard(model.text);
}

/**
 * Is the text Cut is about to delete still the selected text? If the
 * selection could not be put back, `delete` would act as Backspace on the
 * caret and eat a character the user never selected.
 */
function selectionStillMatches(model) {
  if (model.kind === 'field') {
    const { el } = model;
    return String(el.value ?? '').slice(el.selectionStart ?? 0, el.selectionEnd ?? 0) === model.text;
  }
  return String(window.getSelection() || '') === model.text;
}

export async function cutAction(model) {
  if (!model.text || !model.el) return;
  await copyTextToClipboard(model.text);
  restoreSelection(model);
  if (selectionStillMatches(model)) insertText(model.el, '');
}

export async function pasteAction(model) {
  if (!model.el) return;
  const text = await readClipboardText();
  if (!text) return;
  restoreSelection(model);
  if (!dispatchPaste(model.el, text)) insertText(model.el, text);
}

export function selectAllAction(model) {
  const { el } = model;
  if (!el) return;
  try {
    el.focus({ preventScroll: true });
  } catch {
    // ignore
  }
  if (model.kind === 'field') {
    el.select();
    return;
  }
  const range = document.createRange();
  range.selectNodeContents(el);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}
